import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { useConfirm } from "../../components/ConfirmDialog";
import { INTENT, fmtDateTime } from "../../lib/helpers";
import AssistantAvatar from "../../components/AssistantAvatar";

// Page de l'assistant mira.ia, pour l'agence seule (décisions de Mélilia du 02/10) : le bouton qui coupe et
// rétablit son accès (sa clé reste valable), et le journal de ce qu'il a lu et écrit. Le nom vient du serveur
// (core.ASSISTANT_NAME), seul endroit où il s'écrit.

// Ce que l'assistant a consulté ou modifié, dit pour l'agence (une ligne par famille de routes/assistant.py).
const RESOURCE_LABELS = {
  message_projet: "Message dans la conversation du projet",
  message_mission: "Message dans la conversation de la mission",
  traite_projet: "Message du projet marqué traité",
  traite_mission: "Message de la mission marqué traité",
  demande: "État d'une demande",
  note_entreprise: "Note interne ajoutée (entreprise)",
  note_collaborateur: "Note interne ajoutée (collaborateur)",
  note_contact: "Note ajoutée (contact)",
  coordonnees_entreprise: "Coordonnées d'une entreprise",
  coordonnees_collaborateur: "Coordonnées d'un collaborateur",
  contact_cree: "Contact créé",
  contact_modifie: "Contact modifié",
  etape: "État d'une étape",
  phase_projet: "État d'une phase de projet",
  phase_mission: "État d'une phase de mission",
  publication_creee: "Publication créée",
  publication_modifiee: "Publication modifiée",
  echeance_creee: "Échéance créée",
  echeance_modifiee: "Échéance modifiée",
  paiement: "Paiement enregistré",
  remuneration_creee: "Rémunération créée",
  remuneration_modifiee: "Rémunération modifiée",
  identite: "Vérification de sa connexion",
  projets: "Liste des projets",
  projet: "Projet",
  missions: "Liste des missions",
  mission: "Mission",
  a_traiter: "« À traiter »",
  demandes: "Demandes",
  entreprises: "Liste des entreprises",
  entreprise: "Fiche entreprise",
  contacts: "Contacts",
  collaborateurs: "Liste des collaborateurs",
  collaborateur: "Fiche collaborateur",
  publications: "Calendrier éditorial",
  workflows: "Workflows",
  regles: "Règles d'automatisation",
  fichier: "Fichier",
};

const Pill = ({ intent, children }) => (
  <span className={`inline-flex items-center text-xs font-semibold leading-none px-2.5 py-1 rounded-sm ${INTENT[intent].cls}`}>{children}</span>
);

export default function AdminAssistant() {
  const { confirm } = useConfirm();
  const [state, setState] = useState(null);
  const [lines, setLines] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, j] = await Promise.all([api.get("/admin/assistant"), api.get("/admin/assistant/journal")]);
      setState(s.data);
      setLines(j.data);
    } catch {
      toast.error("Chargement impossible");
      setState((prev) => prev || { error: true });
      setLines((prev) => prev || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setAccess = async (enabled) => {
    const name = state.name;
    if (!enabled && !(await confirm({
      title: `Couper l'accès de ${name} ?`,
      message: `${name} ne pourra plus rien lire ni écrire dans Horizon tant que vous ne rétablissez pas l'accès. Sa clé reste valable.`,
      confirmLabel: "Couper l'accès",
      tone: "danger",
    }))) return;
    setBusy(true);
    try {
      await api.put("/admin/assistant/access", { enabled });
      toast.success(enabled ? "Accès rétabli" : "Accès coupé");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modification impossible");
    } finally { setBusy(false); }
  };

  if (!state) {
    return (
      <div>
        <PageTitle overline="Automatisations" title="Assistant" />
        <div className="bg-sable rounded-sm border border-marine/10 p-5 h-32 animate-pulse" data-testid="assistant-loading" />
      </div>
    );
  }

  const name = state.name || "Assistant";
  const status = !state.key_present ? { intent: "inerte", label: "Aucune clé" }
    : state.enabled ? { intent: "en_cours", label: "Accès ouvert" } : { intent: "inerte", label: "Accès coupé" };

  return (
    <div data-testid="assistant-page">
      <PageTitle overline="Automatisations" title={name} />
      <p className="text-sm text-marine/80 mb-5 max-w-3xl">
        L'assistant de Metara. Vous gardez la main : couper son accès l'arrête aussitôt, sans toucher à sa clé. Tout ce qu'il lit et
        écrit est consigné ci-dessous. Seule l'agence voit cette page.
      </p>

      <div className="grid grid-cols-1 gap-5">
        <section className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5 sm:p-6" data-testid="assistant-access">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <AssistantAvatar height={56} className="-mt-1" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-display text-xl text-marine">Accès</h2>
                  <span data-testid="assistant-status"><Pill intent={status.intent}>{status.label}</Pill></span>
                </div>
                <ul className="text-sm text-marine/80 mt-2 grid grid-cols-1 gap-1">
                  {state.key_present
                    ? <li>Clé créée le {fmtDateTime(state.key_created_at)}.</li>
                    : <li>{name} n'a pas encore de clé pour cette base : son accès ne peut pas être ouvert d'ici.</li>}
                  {state.enabled_changed_at && (
                    <li>{state.enabled ? "Rétabli" : "Coupé"} le {fmtDateTime(state.enabled_changed_at)}{state.enabled_changed_by ? ` par ${state.enabled_changed_by}` : ""}.</li>
                  )}
                  <li>Dernière activité : {state.last_activity_at ? fmtDateTime(state.last_activity_at) : "aucune"}.</li>
                </ul>
              </div>
            </div>
            {state.key_present && (state.enabled ? (
              <button type="button" onClick={() => setAccess(false)} disabled={busy} data-testid="assistant-cut"
                className="text-sm font-medium text-rouge bg-white border border-rouge/40 rounded-sm px-4 py-2 hover:bg-rouge-50 transition-colors disabled:opacity-60">
                Couper l'accès
              </button>
            ) : (
              <button type="button" onClick={() => setAccess(true)} disabled={busy} data-testid="assistant-restore"
                className="text-sm font-medium text-white bg-marine rounded-sm px-4 py-2 hover:bg-marine-hover transition-colors disabled:opacity-60">
                Rétablir l'accès
              </button>
            ))}
          </div>
        </section>

        <section className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5 sm:p-6" data-testid="assistant-journal">
          <h2 className="font-display text-xl text-marine">Ce que {name} a lu et écrit</h2>
          <p className="text-sm text-marine/80 mt-1 mb-4">Les 500 dernières lignes, de la plus récente à la plus ancienne.</p>
          {lines && lines.length === 0 && <p className="text-sm text-marine/80">Rien pour l'instant : {name} n'a encore rien lu ni écrit.</p>}
          {lines && lines.length > 0 && (
            <ul className="grid grid-cols-1 divide-y divide-marine/10 border-y border-marine/10">
              {lines.map((l) => (
                <li key={l.id} className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid="assistant-journal-line" title={l.path || ""}>
                  <span className="text-marine/80 tabular-nums w-36 shrink-0">{fmtDateTime(l.at)}</span>
                  <span className="inline-flex items-center text-[11px] font-semibold leading-none px-2 py-1 rounded-full border border-marine/25 text-marine bg-creme">
                    {l.action === "écriture" ? "Écriture" : "Lecture"}
                  </span>
                  <span className="text-marine min-w-0 break-words">
                    {RESOURCE_LABELS[l.resource_type] || l.resource_type}{l.label ? ` · ${l.label}` : (l.resource_id ? ` · ${l.resource_id}` : "")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
